/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-01-20 09:04:57
 *
 *******************************************************/
// @flow

import type { MerchandisingState, MerchandisingAction } from 'stores/flow-type/merchandising.type';

import {
  GET_MERCHANDISING_REQUEST,
  GET_MERCHANDISING_SUCCESS,
  GET_MERCHANDISING_FAILURE,
  UPDATE_MERCHANDISING_RULE_REQUEST,
  UPDATE_MERCHANDISING_RULE_SUCCESS,
  UPDATE_MERCHANDISING_RULE_FAILURE,
  DELETE_MERCHANDISING_RULE_REQUEST,
  DELETE_MERCHANDISING_RULE_SUCCESS,
  DELETE_MERCHANDISING_RULE_FAILURE,
  REORDER_MERCHANDISING_RULES_REQUEST,
  REORDER_MERCHANDISING_RULES_SUCCESS,
  REORDER_MERCHANDISING_RULES_FAILURE,
  GET_MERCHANDISING_RULE_NAMES_REQUEST,
  GET_MERCHANDISING_RULE_NAMES_SUCCESS,
  GET_MERCHANDISING_RULE_NAMES_FAILURE,
  CREATE_MERCHANDISING_REQUEST,
  CREATE_MERCHANDISING_SUCCESS,
  CREATE_MERCHANDISING_FAILURE,
  GET_MERCHANDISING_RULE_REQUEST,
  GET_MERCHANDISING_RULE_SUCCESS,
  GET_MERCHANDISING_RULE_FAILURE,
  SHOW_FEEDBACK_VERSION,
  HIDE_FEEDBACK_VERSION,
  SEND_FEEDBACK_MERCHANDISING_VERSION_REQUEST,
  SEND_FEEDBACK_MERCHANDISING_VERSION_SUCCESS,
  SEND_FEEDBACK_MERCHANDISING_VERSION_FAILURE
} from 'stores/flow-type/merchandising.type';

const initialState: MerchandisingState = {
  loading: false,
  saving: false,
  deleting: false,
  reordering: false,
  feedbackLoading: false,
  scheduleSyncIn: 0,
  scheduleSyncAfter: 0,
  waitSyncAt: 0,

  success: false,
  saveSuccess: false,
  error: null,
  merchandisings: [],
  merchandising: {},
  total: 0,
  ruleNames: [],
  feedbackVersion: false
};

const merchandisingReducers = (
  state: MerchandisingState = initialState,
  action: MerchandisingAction
) => {
  switch (action.type) {
    // Get merchandising rules
    case GET_MERCHANDISING_REQUEST:
      return {
        ...state,
        success: false,
        loading: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    case GET_MERCHANDISING_SUCCESS: {
      const { data } = action.payload;
      let { data: merchandisings, total, waitSyncAt, scheduleSyncIn, scheduleSyncAfter } = data;

      return {
        ...state,
        loading: false,
        success: true,
        error: undefined,
        merchandisings,
        merchandising: {}, // Init merchandising rule details
        total,
        waitSyncAt,
        scheduleSyncIn,
        scheduleSyncAfter
      };
    }
    case GET_MERCHANDISING_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };

    // Update merchandising rule
    case UPDATE_MERCHANDISING_RULE_REQUEST:
      return {
        ...state,
        saveSuccess: false,
        saving: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    case UPDATE_MERCHANDISING_RULE_SUCCESS: {
      const { merchandising, actionName, waitSyncAt, scheduleSyncIn, scheduleSyncAfter } =
        action.payload;

      // Save updated merchandising to store
      if (actionName !== 'changeStatus') {
        return {
          ...state,
          saving: false,
          saveSuccess: true,
          merchandising,
          scheduleSyncIn,
          scheduleSyncAfter,
          waitSyncAt,
          error: undefined
        };
      }
      return {
        ...state,
        saving: false,
        saveSuccess: true,
        scheduleSyncIn,
        scheduleSyncAfter,
        waitSyncAt,
        error: undefined
      };
    }
    case UPDATE_MERCHANDISING_RULE_FAILURE:
      return {
        ...state,
        saving: false,
        saveSuccess: false,
        error: action.payload
      };

    // Delete merchandising rule
    case DELETE_MERCHANDISING_RULE_REQUEST:
      return {
        ...state,
        success: false,
        deleting: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    case DELETE_MERCHANDISING_RULE_SUCCESS: {
      const { scheduleSyncIn, scheduleSyncAfter, waitSyncAt } = action.payload;
      return {
        ...state,
        deleting: false,
        success: true,
        scheduleSyncIn,
        scheduleSyncAfter,
        waitSyncAt,
        total: state.total - 1,
        error: undefined
      };
    }
    case DELETE_MERCHANDISING_RULE_FAILURE:
      return {
        ...state,
        deleting: false,
        success: false,
        error: action.payload
      };

    // Reorder merchandising rule
    case REORDER_MERCHANDISING_RULES_REQUEST:
      return {
        ...state,
        success: false,
        reordering: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    case REORDER_MERCHANDISING_RULES_SUCCESS: {
      const { scheduleSyncIn, scheduleSyncAfter, waitSyncAt } = action.payload;
      return {
        ...state,
        reordering: false,
        success: true,
        scheduleSyncIn,
        scheduleSyncAfter,
        waitSyncAt,
        error: undefined
      };
    }
    case REORDER_MERCHANDISING_RULES_FAILURE:
      return {
        ...state,
        reordering: false,
        success: false,
        error: action.payload
      };

    // Get merchandising rule names
    case GET_MERCHANDISING_RULE_NAMES_REQUEST:
      return { ...state, success: false, loading: true };
    case GET_MERCHANDISING_RULE_NAMES_SUCCESS: {
      const { data } = action.payload;
      let { data: ruleNames, total } = data;

      return {
        ...state,
        loading: false,
        success: true,
        error: undefined,
        ruleNames,
        total
      };
    }
    case GET_MERCHANDISING_RULE_NAMES_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };

    // Create merchandising rule
    case CREATE_MERCHANDISING_REQUEST:
      return {
        ...state,
        saveSuccess: false,
        saving: true,
        waitSyncAt: 0,
        scheduleSyncIn: 0,
        scheduleSyncAfter: 0
      };
    case CREATE_MERCHANDISING_SUCCESS: {
      const { merchandising, scheduleSyncIn, scheduleSyncAfter, waitSyncAt } = action.payload;

      return {
        ...state,
        saving: false,
        merchandising,
        saveSuccess: true,
        scheduleSyncIn,
        scheduleSyncAfter,
        waitSyncAt,
        error: undefined
      };
    }
    case CREATE_MERCHANDISING_FAILURE:
      return {
        ...state,
        saving: false,
        saveSuccess: false,
        error: action.payload
      };

    // Get merchandising rule
    case GET_MERCHANDISING_RULE_REQUEST:
      return { ...state, success: false, loading: true };
    case GET_MERCHANDISING_RULE_SUCCESS: {
      const { data } = action.payload;
      return {
        ...state,
        loading: false,
        success: true,
        merchandising: data,
        error: undefined
      };
    }
    case GET_MERCHANDISING_RULE_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };
    case SHOW_FEEDBACK_VERSION:
      return {
        ...state,
        feedbackVersion: true
      };
    case HIDE_FEEDBACK_VERSION:
      return {
        ...state,
        feedbackVersion: false
      };

    // Feedback version
    case SEND_FEEDBACK_MERCHANDISING_VERSION_REQUEST:
      return {
        ...state,
        success: false,
        feedbackLoading: true
      };
    case SEND_FEEDBACK_MERCHANDISING_VERSION_SUCCESS: {
      return {
        ...state,
        feedbackLoading: false,
        success: true,
        error: undefined
      };
    }
    case SEND_FEEDBACK_MERCHANDISING_VERSION_FAILURE:
      return {
        ...state,
        feedbackLoading: false,
        success: false,
        error: action.payload
      };
    default:
      return state;
  }
};
export default merchandisingReducers;
